import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CaretDown, CaretRight, Copy, Lightning, Plus, UploadSimple, Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// Same tracker base the publisher portal advertises (TrackingLinksTab.js).
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const TRACKER_DOMAIN = process.env.REACT_APP_TRACKER_DOMAIN || "";
const TRACKER_BASE = TRACKER_DOMAIN
  ? `${TRACKER_DOMAIN.replace(/\/$/, "")}/c`
  : `${BASE_URL}/api/click`;

const trackerUrl = (brandId, subId) => `${TRACKER_BASE}/${brandId}?subId1=${subId || "{subId1}"}`;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600",
  paused: "bg-amber-100 text-amber-700",
};

function Pill({ value }) {
  const v = (value || "").toLowerCase();
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_STYLES[v] || "bg-slate-100 text-slate-600"}`}>
      {v || "—"}
    </span>
  );
}

function Step({ n, title, hint, children }) {
  return (
    <section className="border border-slate-200 rounded-md p-4 mb-6 bg-white">
      <div className="flex items-baseline gap-3 mb-1">
        <span className="text-[10px] font-bold text-red-700 tracking-[0.2em]">STEP {n}</span>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      {hint && <p className="text-xs text-slate-500 mb-3 max-w-3xl">{hint}</p>}
      {children}
    </section>
  );
}

function Tile({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-mono mt-1 text-sm">{children}</div>
    </div>
  );
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Copy failed");
  }
}

export default function AdminRga() {
  const [health, setHealth] = useState(null);
  const [postbackInfo, setPostbackInfo] = useState(null);
  const [brands, setBrands] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Step 1 — placements
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [manual, setManual] = useState({ tracking_url: "", name: "" });

  // Step 2 — assignment
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [assign, setAssign] = useState({ publisher_id: "", payout_mode: "passthrough", pub_split_pct: "", pub_flat_amount: "" });
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, pb, b, p] = await Promise.all([
        API.get("/admin/rga/health"),
        API.get("/admin/rga/postback-info"),
        API.get("/admin/rga/brands"),
        API.get("/admin/publishers"),
      ]);
      setHealth(h.data);
      setPostbackInfo(pb.data);
      setBrands(b.data.items || []);
      const pubs = Array.isArray(p.data) ? p.data : (p.data?.items || []);
      setPublishers(pubs.filter((u) => (u.role || "publisher") === "publisher"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load ReallyGreatAds");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) =>
      [b.name, b.advertiser_name, b.campaign_id, b.rga_placement_id]
        .some((v) => String(v || "").toLowerCase().includes(q)));
  }, [brands, search]);

  // ── Step 1 ────────────────────────────────────────────────────────
  const uploadCsv = async (dryRun) => {
    if (!csvFile) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", csvFile);
      const { data } = await API.post(`/admin/rga/placements/upload?dry_run=${dryRun}`, form);
      if (dryRun) {
        setCsvPreview(data);
      } else {
        toast.success(`Brands created ${data.to_create} · updated ${data.to_update} · skipped ${data.skipped.length}`);
        setCsvPreview(null);
        setCsvFile(null);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/admin/rga/brands", manual);
      toast.success(`${data.created ? "Created" : "Updated"} brand ${data.brand?.campaign_id}`);
      setManual({ tracking_url: "", name: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    }
  };

  // ── Step 2 ────────────────────────────────────────────────────────
  const toggle = (setFn, id) => setFn((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allVisibleSelected = visibleBrands.length > 0 && visibleBrands.every((b) => selected.has(b.campaign_id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    visibleBrands.forEach((b) => (allVisibleSelected ? next.delete(b.campaign_id) : next.add(b.campaign_id)));
    return next;
  });

  const doAssign = async () => {
    const brandIds = [...selected];
    // bulk-assign treats a missing/empty brand_ids as "every brand in the
    // catalog" — never send it without an explicit list.
    if (brandIds.length === 0 || !assign.publisher_id) return;
    const body = {
      publisher_id: assign.publisher_id,
      brand_ids: brandIds,
      payout_mode: assign.payout_mode,
    };
    if (assign.pub_split_pct !== "") body.pub_split_pct = Number(assign.pub_split_pct);
    if (assign.payout_mode === "flat_cpa") body.pub_flat_amount = Number(assign.pub_flat_amount);
    setAssigning(true);
    try {
      const { data } = await API.post("/admin/publisher-brands/bulk-assign", body);
      toast.success(`Assigned ${data.assigned} · skipped ${data.skipped}`);
      (data.skip_details || []).slice(0, 5).forEach((s) => toast.message(`${s.brand_name || s.brand_id}: ${s.reason}`));
      setSelected(new Set());
      setExpanded(new Set(brandIds));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const assignDisabled = assigning || selected.size === 0 || !assign.publisher_id
    || (assign.payout_mode === "flat_cpa" && !(Number(assign.pub_flat_amount) > 0));

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-rga-page">
      <div className="mb-6">
        <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-red-700">Placements → publishers → webhooks</p>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">ReallyGreatAds</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Each RGA placement is one brand. Upload placements, assign them to publishers, and conversions
          sync back automatically by webhook — RGA has no API, so nothing is pulled from their portal.
        </p>
      </div>

      <div className="border border-slate-200 rounded-md p-4 mb-6 bg-white grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Brands">{health?.brand_count ?? "—"}</Tile>
        <Tile label="Publisher assignments">{health?.assignment_count ?? "—"}</Tile>
        <Tile label="Conversions (all time)">{health?.action_count ?? "—"}</Tile>
        <Tile label="Last upload">
          {health?.last_upload
            ? `${(health.last_upload.uploaded_at || "").slice(0, 16).replace("T", " ")} · +${health.last_upload.created}`
            : "never"}
        </Tile>
      </div>

      <Step n={1} title="Add placements as brands"
        hint={`Upload the placements export from RGA, one row per placement. Each row needs its tracking URL (RGA uses several domains, e.g. ${(health?.tracking_hosts || ["go.news247info.com"]).join(", ")}) — it is stored exactly as given. Placement id, name, type, status, merchant, destination URL, payout min/max and keyword columns are used when present. A Paused/Inactive status makes the brand inactive. Placements not in the file are left unchanged.`}>
        <div className="flex flex-wrap gap-3 items-center">
          <Input type="file" accept=".csv,text/csv" className="h-9 max-w-xs"
            onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setCsvPreview(null); }} />
          <Button variant="outline" disabled={!csvFile || uploading} onClick={() => uploadCsv(true)}>
            <UploadSimple size={14} className="mr-1" /> Preview
          </Button>
          <Button className="bg-red-700 hover:bg-red-800 text-white"
            disabled={!csvPreview || uploading || csvPreview.placements.length === 0}
            onClick={() => uploadCsv(false)}>
            Create {csvPreview?.to_create ?? 0} · update {csvPreview?.to_update ?? 0} brands
          </Button>
        </div>

        {csvPreview && (
          <div className="mt-4">
            {csvPreview.skipped.length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                {csvPreview.skipped.length} row(s) will be skipped:
                {csvPreview.skipped.slice(0, 10).map((r) => (
                  <div key={r.line} className="font-mono">line {r.line}: {r.reason}</div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5">Action</th><th className="px-2 py-1.5">Brand id</th>
                    <th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Merchant</th>
                    <th className="px-2 py-1.5">Type</th><th className="px-2 py-1.5">Status</th>
                    <th className="px-2 py-1.5">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.placements.map((p) => (
                    <tr key={p.placement_id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-semibold">{p.action}</td>
                      <td className="px-2 py-1.5 font-mono">{p.campaign_id}</td>
                      <td className="px-2 py-1.5">{p.name || "—"}</td>
                      <td className="px-2 py-1.5">{p.merchant || "—"}</td>
                      <td className="px-2 py-1.5">{p.type || "—"}</td>
                      <td className="px-2 py-1.5">{p.status || "—"}</td>
                      <td className="px-2 py-1.5">{p.payout || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <form onSubmit={addManual} className="flex flex-wrap gap-3 items-end mt-4 pt-4 border-t border-slate-100">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs">Or add one: tracking URL</Label>
            <Input value={manual.tracking_url} placeholder="https://go.news247info.com/<placement-uuid>"
              onChange={(e) => setManual({ ...manual, tracking_url: e.target.value })} className="h-9" />
          </div>
          <div className="min-w-[200px]">
            <Label className="text-xs">Brand name</Label>
            <Input value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} className="h-9" />
          </div>
          <Button type="submit" variant="outline" disabled={!manual.tracking_url.trim()}>
            <Plus size={14} className="mr-1" /> Add brand
          </Button>
        </form>
      </Step>

      <Step n={2} title="Assign brands to publishers"
        hint="Tick brands, pick a publisher and payout, then assign. Passthrough pays the publisher their split of RGA's payout. Expand a brand to see who runs it, their tracking link, and last-30-day clicks, RGA-validated clicks and conversions.">
        <div className="flex flex-wrap gap-3 items-end mb-4 p-3 rounded bg-slate-50 border border-slate-200">
          <div className="min-w-[240px]">
            <Label className="text-xs">Publisher</Label>
            <Select value={assign.publisher_id} onValueChange={(v) => setAssign({ ...assign, publisher_id: v })}>
              <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="Choose publisher" /></SelectTrigger>
              <SelectContent>
                {publishers.map((p) => {
                  const id = String(p.id || p._id || "");
                  if (!id) return null;
                  const label = p.company_name || p.name || p.email || id;
                  return (
                    <SelectItem key={id} value={id}>
                      {label + (p.sub_id ? ` · ${p.sub_id}` : "")}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label className="text-xs">Payout</Label>
            <Select value={assign.payout_mode} onValueChange={(v) => setAssign({ ...assign, payout_mode: v })}>
              <SelectTrigger className="h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="passthrough">Passthrough (split)</SelectItem>
                <SelectItem value="flat_cpa">Flat CPA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-28">
            <Label className="text-xs">Pub split %</Label>
            <Input type="number" min="0" max="100" placeholder="default" className="h-9 bg-white"
              value={assign.pub_split_pct} onChange={(e) => setAssign({ ...assign, pub_split_pct: e.target.value })} />
          </div>
          {assign.payout_mode === "flat_cpa" && (
            <div className="w-28">
              <Label className="text-xs">Flat $</Label>
              <Input type="number" min="0" step="0.01" className="h-9 bg-white"
                value={assign.pub_flat_amount} onChange={(e) => setAssign({ ...assign, pub_flat_amount: e.target.value })} />
            </div>
          )}
          <Button className="bg-red-700 hover:bg-red-800 text-white" disabled={assignDisabled} onClick={doAssign}>
            <Users size={14} className="mr-1" />
            {assigning ? "Assigning…" : `Assign ${selected.size} brand${selected.size === 1 ? "" : "s"}`}
          </Button>
        </div>

        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search brands"
          className="h-9 max-w-xs mb-3" />

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" />
                </th>
                <th className="px-3 py-2">Brand</th>
                <th className="px-3 py-2">Placement</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Publishers</th>
                <th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">RGA valid</th>
                <th className="px-3 py-2 text-right">Conv.</th>
                <th className="px-3 py-2 text-right">Payout</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : visibleBrands.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No brands yet — upload placements in step 1.</td></tr>
              ) : visibleBrands.map((b) => {
                const open = expanded.has(b.campaign_id);
                return (
                  <Fragment key={b.campaign_id}>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selected.has(b.campaign_id)}
                          onChange={() => toggle(setSelected, b.campaign_id)} aria-label={`Select ${b.name}`} />
                      </td>
                      <td className="px-3 py-2">
                        <button type="button" className="flex items-center gap-1 text-left"
                          onClick={() => toggle(setExpanded, b.campaign_id)}>
                          {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
                          <span>{b.name}</span>
                        </button>
                        <div className="text-[11px] text-slate-400 font-mono ml-4">{b.campaign_id}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-mono font-medium truncate max-w-[140px]" title={b.rga_placement_id}>
                          {/^\d+$/.test(b.rga_placement_id) ? `#${b.rga_placement_id}` : (b.rga_placement_id || "—")}
                        </div>
                        <div className="text-[11px] font-semibold text-slate-500">{b.rga_placement_type || "Placement"}</div>
                      </td>
                      <td className="px-3 py-2"><Pill value={b.status} /></td>
                      <td className="px-3 py-2 text-xs">{b.assignments.length}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{b.totals.clicks}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{b.totals.valid}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{b.totals.conversions}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {b.totals.payout > 0 ? money(b.totals.payout) : ((b.payout_display || "—").replace(/[\s\–\—\-]+$/, "").trim() || "—")}
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-50/60">
                        <td />
                        <td colSpan={8} className="px-3 py-3">
                          <div className="mb-3 p-3 bg-white border border-slate-200 rounded-md flex flex-wrap gap-4 items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-700">
                                Placement {/^\d+$/.test(b.rga_placement_id) ? `#${b.rga_placement_id}` : b.rga_placement_id}
                              </span>
                              {b.rga_placement_uuid && b.rga_placement_uuid !== b.rga_placement_id && (
                                <button type="button" onClick={() => copyText(b.rga_placement_uuid)}
                                        className="font-mono text-[11px] text-slate-500 bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded border border-slate-200 transition-colors flex items-center gap-1"
                                        title="Click to copy original Placement UUID">
                                  <Copy size={11} /> UUID: {b.rga_placement_uuid}
                                </button>
                              )}
                              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                (b.rga_placement_type || "").toLowerCase().includes("deep")
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}>
                                {b.rga_placement_type || "Direct Link"}
                              </span>
                            </div>

                            <div className="w-full flex items-center gap-2" data-testid={`rga-payout-${b.campaign_id}`}>
                              <span className="font-semibold text-slate-700 whitespace-nowrap">Payout</span>
                              <Input
                                defaultValue={b.payout_display || ""}
                                placeholder="e.g. $22 – $350 or 5%  (empty = use imported payout)"
                                className="h-8 text-xs"
                                onBlur={async (e) => {
                                  const v = e.target.value.trim();
                                  if (v === (b.payout_display || "")) return;
                                  try {
                                    await API.put(`/admin/rga/brands/${encodeURIComponent(b.campaign_id)}/payout`, { payout: v });
                                    toast.success(v ? "Payout saved" : "Payout override cleared");
                                    load();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Save failed");
                                  }
                                }}
                              />
                            </div>
                            <div className="w-full flex items-center gap-2" data-testid={`rga-deeplink-${b.campaign_id}`}>
                              <span className="font-semibold text-slate-700 whitespace-nowrap">Deeplink template</span>
                              <Input
                                defaultValue={b.deeplink_template || ""}
                                placeholder="https://go.news247info.com/…?dest=[url]  (empty = no deep links)"
                                className="h-8 text-xs font-mono"
                                onBlur={async (e) => {
                                  const v = e.target.value.trim();
                                  if (v === (b.deeplink_template || "")) return;
                                  try {
                                    await API.put(`/admin/rga/brands/${encodeURIComponent(b.campaign_id)}/deeplink-template`, { deeplink_template: v });
                                    toast.success(v ? "Deeplink template saved" : "Deeplink template cleared");
                                    load();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Save failed");
                                  }
                                }}
                              />
                            </div>

                            {b.rga_destination_url && (
                              <div className="flex items-center gap-1.5 max-w-lg truncate">
                                <span className="text-slate-400 font-medium shrink-0">Destination URL:</span>
                                <a href={b.rga_destination_url} target="_blank" rel="noopener noreferrer" 
                                   className="text-blue-600 hover:underline truncate font-mono text-[11px]">
                                  {b.rga_destination_url}
                                </a>
                                <button type="button" onClick={() => copyText(b.rga_destination_url)} className="text-slate-400 hover:text-slate-600 shrink-0">
                                  <Copy size={12} />
                                </button>
                              </div>
                            )}

                            {b.tracking_link && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-400 font-medium">Direct Tracking Link:</span>
                                <button type="button" onClick={() => copyText(b.tracking_link)} className="font-mono text-[11px] text-slate-600 hover:text-slate-900 flex items-center gap-1">
                                  <Copy size={12} /> {b.tracking_link}
                                </button>
                              </div>
                            )}
                          </div>

                          {Array.isArray(b.rga_keywords) && b.rga_keywords.length > 0 && (
                            <div className="mb-3 p-3 bg-white border border-slate-200 rounded-md">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-slate-700">Suggested Keywords ({b.rga_keywords.length})</span>
                                <button type="button" onClick={() => copyText(b.rga_keywords.join("; "))} className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                                  <Copy size={12} /> Copy All Keywords
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                                {b.rga_keywords.map((kw, i) => (
                                  <span key={i} onClick={() => copyText(kw)} title="Click to copy keyword"
                                        className="cursor-pointer text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-0.5 rounded border border-slate-200 transition-colors">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {b.assignments.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">Not assigned to any publisher yet.</p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="py-1 pr-3">Publisher</th><th className="py-1 pr-3">Status</th>
                                  <th className="py-1 pr-3">Payout</th><th className="py-1 pr-3">Tracking link</th>
                                  <th className="py-1 pr-3 text-right">Clicks</th><th className="py-1 pr-3 text-right">Valid</th>
                                  <th className="py-1 pr-3 text-right">Conv.</th><th className="py-1 text-right">Pub earning</th>
                                </tr>
                              </thead>
                              <tbody>
                                {b.assignments.map((a) => {
                                  const url = trackerUrl(b.campaign_id, a.sub_id);
                                  return (
                                    <tr key={a.publisher_id} className="border-t border-slate-200/70">
                                      <td className="py-1.5 pr-3">{a.publisher_name}<div className="text-slate-400 font-mono">{a.sub_id}</div></td>
                                      <td className="py-1.5 pr-3"><Pill value={a.status} /></td>
                                      <td className="py-1.5 pr-3">
                                        {a.payout_mode === "flat_cpa" ? money(a.pub_flat_amount) : `${a.pub_split_pct ?? "—"}% split`}
                                      </td>
                                      <td className="py-1.5 pr-3">
                                        <button type="button" onClick={() => copyText(url)}
                                          className="flex items-center gap-1 font-mono text-[11px] text-slate-600 hover:text-slate-900 break-all text-left">
                                          <Copy size={12} className="shrink-0" /> {url}
                                        </button>
                                      </td>
                                      <td className="py-1.5 pr-3 text-right font-mono">{a.clicks}</td>
                                      <td className="py-1.5 pr-3 text-right font-mono">{a.valid}</td>
                                      <td className="py-1.5 pr-3 text-right font-mono">{a.conversions}</td>
                                      <td className="py-1.5 text-right font-mono">{money(a.earning)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                          {b.totals.clicks > b.assignments.reduce((n, a) => n + a.clicks, 0) && (
                            <p className="text-[11px] text-amber-700 mt-2">
                              Some clicks on this brand could not be matched to an assigned publisher (bad or missing subId1).
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Step>

      <Step n={3} title="Connect the webhooks (once)"
        hint="This is how RGA updates reach us. In the RGA portal → Webhooks, add one webhook per event type with the URL below. When the form lists the {placeholders}, map each to the internal value shown. Valid Click marks our click as accepted by RGA; Confirmed Click creates the conversion and pays the publisher.">
        {(postbackInfo?.webhooks || []).map((w) => (
          <div key={w.event_type} className="mb-3">
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Lightning size={14} /> {w.event_type}
              </span>
              <Button size="sm" variant="outline" onClick={() => copyText(w.url_template)}>
                <Copy size={14} className="mr-1" /> Copy
              </Button>
            </div>
            <code className="block text-[11px] font-mono break-all text-slate-700 bg-slate-50 border border-slate-200 rounded p-2">
              {w.url_template}
            </code>
          </div>
        ))}
        <p className="text-xs text-slate-500">
          Map: {Object.entries(postbackInfo?.macro_mapping || {}).map(([k, v]) => `${k} → ${v}`).join(" · ")}.
          {" "}We send sub1 = our click id, sub2 = publisher subId1, sub5 = placement id on every click.
        </p>
      </Step>
    </div>
  );
}
